import { useTranslation } from 'react-i18next';

/**
 * Custom hook that provides a simpler interface for translations
 * This replaces the direct access to i18n objects from amdi18n-loader
 * 
 * Usage examples:
 * const { t } = useI18n('views'); // Use views namespace
 * const { t } = useI18n(); // Use default namespace (views)
 * const { t, i18n } = useI18n(['views', 'common']); // Multiple namespaces
 * 
 * Then use: t('searchTable') or t('common:loading')
 */
export const useI18n = (ns = 'views') => {
  const { t, i18n } = useTranslation(ns);
  
  return {
    t,
    i18n,
    // Convenience methods for different namespaces
    tViews: (key, options) => i18n.t(key, { ...options, ns: 'views' }),
    tCommon: (key, options) => i18n.t(key, { ...options, ns: 'common' }),
    tUtils: (key, options) => i18n.t(key, { ...options, ns: 'utils' }),
    tGenerics: (key, options) => i18n.t(key, { ...options, ns: 'generics' }),
    tConfig: (key, options) => i18n.t(key, { ...options, ns: 'config' })
  };
};

/**
 * Higher-order component that provides i18n context to class components
 * For function components, use the useI18n hook directly instead
 * Access in class components via: this.props.i18n.tViews('key')
 */
export const withI18n = (WrappedComponent, namespaces = ['views']) => {
  return function WithI18nComponent(props) {
    const i18nProps = useI18n(namespaces);
    return <WrappedComponent {...props} i18n={i18nProps} />;
  };
};

export default useI18n;